import { Divider, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../../components/base/BasicDialog";
import { AddReservationDialog } from "../../../components/dialog/AddReservationDialog/AddReservationDialog";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { useCustomerById } from "../../../services/customersService";
import {
    AgreementConfigurationDialog,
    AgreementConfigurationDialogMode,
} from "../../AgreementsPage/AgreementConfigurationDialog/AgreementConfigurationDialog";
import { ClientPassportLeadsTable } from "../../LeadsPage/ClientPassportLeadsTable";
import { OfferCreateDialog } from "../../OffersPage/OfferDialogs";
import { CreateTaskDialog } from "../../TaskPage/CreateTaskDialog";
import { CounterpartyInfoForm } from "./CounterpartyInfoForm";
import { PassportAccordionItem } from "./accordion/PassportAccordionItem";
import { ClientPassportAgreementsTable } from "./tables/ClientPassportAgreementsTable";
import { ClientPassportBookingsTable } from "./tables/ClientPassportBookingsTable";
import { ClientPassportContactsTable } from "./tables/ClientPassportContactsTable";
import { ClientPassportOffersTable } from "./tables/ClientPassportOffersTable";
import { ClientPassportTasksTable } from "./tables/ClientPassportTasksTable";
import IconButtonWithTooltip from "../../../components/button/IconButtonWithTooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

export function CounterpartyPassportDialog(props) {
    const { counterpartyId } = props;
    const { t } = useTranslation();
    const navigate = useNavigate()
    const { openDialog } = useDialogBetter();

    const {
        data: counterpartyData,
        isLoading,
        isFetching,
        refetch,
    } = useCustomerById(counterpartyId);

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            showTitle={false}
            maxWidth={"lg"}
            showDialogActions
            alwaysFullScreen={true}
            headerComponent={
                <Grid
                    container
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    sx={{ p: 2 }}
                >   
                <Grid item xs={5}>
                    <IconButtonWithTooltip
                        title={t("back")}
                        onClick={()=>navigate("/customers/")}
                    >
                        <ArrowBackIcon sx={{ color: "var(--primary)" }} fontSize="large" />
                    </IconButtonWithTooltip>
                </Grid>
                <Grid item xs={5}>
                    <Typography variant={"h4"} sx={{ fontWeight: "semibold" }}>
                        {t("clients.passportDialogCompany.title")}
                    </Typography>
                </Grid>
                <Grid item xs={2}></Grid>
                </Grid>
            }
        >
            <Grid container direction={"column"}>
                <CounterpartyInfoForm
                    counterpartyId={counterpartyId}
                    counterpartyData={counterpartyData}
                    isLoading={isLoading || isFetching}
                />
                <Divider sx={{ my: 2 }} />
                <Grid container direction={"row"} spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <PassportAccordionItem
                            title={t("clients.ClientPassportDialog.contacts")}
                            showAddButton={false}
                        >
                            <ClientPassportContactsTable
                                relatedContactsForClientId={props.counterpartyId}
                            />
                        </PassportAccordionItem>
                    </Grid>
                    <Grid item xs={6}>
                        <PassportAccordionItem
                            title={t("clients.ClientPassportDialog.leads")}
                            showAddButton={false}
                        >
                            <ClientPassportLeadsTable
                                relatedLeadsForClientId={props.counterpartyId}
                            />
                        </PassportAccordionItem>
                    </Grid>

                    <Grid item xs={6}>
                        <PassportAccordionItem
                            title={t("clients.ClientPassportDialog.bookings")}
                            onAddClick={() => {
                                openDialog({
                                    Component: AddReservationDialog,
                                    componentProps: {
                                        initialCustomerIds: [props.counterpartyId],
                                    },
                                    onCloseFn: () => {
                                        refetch();
                                    },
                                });
                            }}
                        >
                            <ClientPassportBookingsTable customerId={props.counterpartyId} />
                        </PassportAccordionItem>
                    </Grid>

                    <Grid item xs={6}>
                        <PassportAccordionItem
                            title={t("clients.ClientPassportDialog.tasks")}
                            onAddClick={() => {
                                openDialog({
                                    Component: CreateTaskDialog,
                                    componentProps: {
                                        customerId: props.counterpartyId || "",
                                    },
                                    onCloseFn: () => {
                                        refetch();
                                    },
                                });
                            }}
                        >
                            <ClientPassportTasksTable
                                relatedTasksForClientId={props.counterpartyId}
                                customerId={props.counterpartyId || ""}
                            />
                        </PassportAccordionItem>
                    </Grid>

                    <Grid item xs={6}>
                        <PassportAccordionItem
                            title={t("clients.ClientPassportDialog.offers")}
                            onAddClick={() => {
                                openDialog({
                                    Component: OfferCreateDialog,
                                    componentProps: {
                                        initialCustomerIds: [props.counterpartyId],
                                    },
                                });
                            }}
                        >
                            <ClientPassportOffersTable customerId={props.counterpartyId} />
                        </PassportAccordionItem>
                    </Grid>

                    <Grid item xs={6}>
                        <PassportAccordionItem
                            title={t("clients.ClientPassportDialog.agreements")}
                            onAddClick={() => {
                                openDialog({
                                    Component: AgreementConfigurationDialog,
                                    componentProps: {
                                        mode: AgreementConfigurationDialogMode.CREATE,
                                        initialCustomerIds: [props.counterpartyId],
                                    },
                                    onCloseFn: () => {
                                        refetch();
                                    },
                                });
                            }}
                        >
                            <ClientPassportAgreementsTable customerId={props.counterpartyId} />
                        </PassportAccordionItem>
                    </Grid>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
